/**
 * Tenant Configuration API Client
 * Task 26: Phase 3 - Frontend Integration
 * 
 * API client for managing per-tenant cloud resource configurations.
 * 
 * CRITICAL - FALLBACK BEHAVIOR:
 * - If tenant config not found → tenant uses core services
 * - Config validation checks actual resource connectivity
 */

import { getAPIClient } from './client';
import type {
  TenantDeploymentConfig,
  TenantConfigSummary,
  TenantConfigValidationResult,
  TenantConfigUpdateRequest,
  TenantConfigCreateRequest,
} from '../types/tenant-config';

// ============================================================================
// GET TENANT CONFIG
// ============================================================================

/**
 * Transform snake_case API response to camelCase TypeScript interface
 */
function transformTenantConfigResponse(data: any): TenantDeploymentConfig {
  return {
    id: data.id,
    tenantId: data.tenant_id,
    deploymentType: data.deployment_type || 'shared',
    cloudProvider: data.cloud_provider || null,
    isActive: data.is_active ?? true,
    resourceStatus: data.resource_status || {},
    // Database
    database: {
      host: data.database_host || null,
      port: data.database_port || 5432,
      database: data.database_name || null,
      user: data.database_user || null,
      password: null, // Never include password
      sslMode: data.database_ssl_mode || 'require',
      poolMinSize: data.database_pool_min_size || 5,
      poolMaxSize: data.database_pool_max_size || 20,
    },
    // Storage
    storage: {
      provider: data.storage_provider || 'azure_blob',
      accountName: data.storage_account_name || null,
      path: data.storage_provider === 'filesystem' ? data.storage_account_name : null,
      containers: data.storage_containers || {},
      connectionString: null, // Never include connection string
      primaryKey: null, // Never include keys
    },
    // Redis
    redis: {
      host: data.redis_host || null,
      port: data.redis_port || 6380,
      password: null, // Never include password
      sslEnabled: data.redis_ssl_enabled ?? true,
      databases: data.redis_databases || {},
    },
    // OpenAI
    openai: {
      provider: data.openai_provider || 'azure_openai',
      endpoint: data.openai_endpoint || null,
      apiKey: null, // Never include API key
      apiVersion: data.openai_api_version || '2024-12-01-preview',
      embeddingDeployment: data.openai_embedding_deployment || null,
      embeddingDimension: data.openai_embedding_dimension || 1536,
      llmDeployment: data.openai_llm_deployment || null,
      llmModelName: data.openai_llm_model_name || null,
    },
    // Monitoring
    monitoring: {
      enabled: false, // Default
      prometheusUrl: null,
      grafanaUrl: null,
    },
    // Azure
    azure: {
      subscriptionId: data.azure_subscription_id || null,
      resourceGroup: data.azure_resource_group || null,
      region: data.azure_region || null,
      keyVaultName: data.azure_key_vault_name || null,
      keyVaultUri: null,
      managedIdentityEnabled: data.azure_managed_identity_enabled || false,
    },
    // Metadata
    configSource: data.config_source || 'database',
    configFilePath: data.config_file_path || null,
    configVersion: data.config_version || null,
    configLastLoadedAt: null,
    createdAt: data.created_at || new Date().toISOString(),
    updatedAt: data.updated_at || new Date().toISOString(),
    createdBy: null,
  };
}

/**
 * Get tenant deployment configuration.
 * Returns null if tenant has no custom config (uses core services).
 */
export async function getTenantConfig(tenantId: string): Promise<TenantDeploymentConfig | null> {
  const client = getAPIClient();
  try {
    const response = await client.get<any>(`/tenants/${tenantId}/config`);
    return transformTenantConfigResponse(response);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      // No custom config - tenant uses core services
      return null;
    }
    throw error;
  }
}

/**
 * Get summary of tenant configs (for admin dashboard).
 */
export async function listTenantConfigSummaries(): Promise<TenantConfigSummary[]> {
  const client = getAPIClient();
  return client.get<TenantConfigSummary[]>('/admin/tenant-configs');
}

// ============================================================================
// UPDATE TENANT CONFIG
// ============================================================================

/**
 * Create tenant deployment configuration.
 * @param tenantId Tenant UUID
 * @param data Config creation data
 */
export async function createTenantConfig(
  tenantId: string,
  data: TenantConfigCreateRequest
): Promise<TenantDeploymentConfig> {
  const client = getAPIClient();
  return client.post<TenantDeploymentConfig>(`/tenants/${tenantId}/config`, data);
}

/**
 * Update tenant deployment configuration (partial update).
 * @param tenantId Tenant UUID
 * @param data Fields to update (only non-undefined fields are updated)
 */
export async function updateTenantConfig(
  tenantId: string,
  data: TenantConfigUpdateRequest
): Promise<TenantDeploymentConfig> {
  const client = getAPIClient();
  return client.put<TenantDeploymentConfig>(`/tenants/${tenantId}/config`, data);
}

/**
 * Delete tenant deployment configuration.
 * After deletion, tenant will use core services.
 */
export async function deleteTenantConfig(tenantId: string): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/tenants/${tenantId}/config`);
}

// ============================================================================
// CONFIG OPERATIONS
// ============================================================================

/**
 * Force reload tenant configuration from source.
 * Invalidates cache and reloads from database/JSON file.
 */
export async function reloadTenantConfig(tenantId: string): Promise<{
  success: boolean;
  message: string;
  usingCoreServices: boolean;
  resourceStatus?: Record<string, string>;
}> {
  const client = getAPIClient();
  return client.post(`/tenants/${tenantId}/config/reload`);
}

/**
 * Validate tenant configuration.
 * Tests actual connections to resources if validateConnections=true.
 */
export async function validateTenantConfig(
  tenantId: string,
  validateConnections: boolean = true
): Promise<TenantConfigValidationResult> {
  const client = getAPIClient();
  return client.post(`/tenants/${tenantId}/config/validate`, null, {
    params: { validate_connections: validateConnections }
  });
}

// ============================================================================
// IMPORT/EXPORT
// ============================================================================

/**
 * Export tenant configuration as JSON.
 * Note: Secrets are NOT included in export.
 */
export async function exportTenantConfig(tenantId: string): Promise<{
  config: Partial<TenantDeploymentConfig>;
  exportedAt: string;
  tenant: { id: string; name: string };
}> {
  const client = getAPIClient();
  return client.get(`/tenants/${tenantId}/config/export`);
}

/**
 * Import tenant configuration from JSON.
 * @param tenantId Target tenant UUID
 * @param config Config data to import
 * @param overwrite Whether to overwrite existing config
 */
export async function importTenantConfig(
  tenantId: string,
  config: TenantConfigUpdateRequest,
  overwrite: boolean = false
): Promise<TenantDeploymentConfig> {
  const client = getAPIClient();
  return client.post(`/tenants/${tenantId}/config/import`, {
    config,
    overwrite,
  });
}

// ============================================================================
// HEALTH CHECKS
// ============================================================================

/**
 * Check health of tenant resources.
 * Returns status for each resource (database, storage, redis, openai).
 */
export async function checkTenantHealth(tenantId: string): Promise<{
  healthy: boolean;
  resources: {
    database: { healthy: boolean; latencyMs?: number; error?: string };
    storage: { healthy: boolean; latencyMs?: number; error?: string };
    redis: { healthy: boolean; latencyMs?: number; error?: string };
    openai: { healthy: boolean; latencyMs?: number; error?: string };
  };
  usingCoreServices: boolean;
  checkedAt: string;
}> {
  const client = getAPIClient();
  return client.get(`/health/tenants/${tenantId}`);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if tenant has custom configuration.
 */
export async function hasTenantConfig(tenantId: string): Promise<boolean> {
  try {
    const config = await getTenantConfig(tenantId);
    return config !== null && config.isActive;
  } catch {
    return false;
  }
}

/**
 * Get resource status for tenant.
 * Returns which resources use custom config vs core services.
 */
export async function getTenantResourceStatus(tenantId: string): Promise<{
  database: 'custom' | 'core';
  storage: 'custom' | 'core';
  redis: 'custom' | 'core';
  openai: 'custom' | 'core';
}> {
  try {
    const config = await getTenantConfig(tenantId);
    if (!config || !config.isActive) {
      return {
        database: 'core',
        storage: 'core',
        redis: 'core',
        openai: 'core',
      };
    }
    return config.resourceStatus as any;
  } catch {
    return {
      database: 'core',
      storage: 'core',
      redis: 'core',
      openai: 'core',
    };
  }
}

/**
 * Get Azure configuration from environment variables.
 * Returns Azure infrastructure config from server environment variables.
 */
export async function getAzureConfigFromEnv(tenantId: string): Promise<{
  azure_subscription_id: string | null;
  azure_resource_group: string | null;
  azure_region: string | null;
  azure_key_vault_name: string | null;
  azure_managed_identity_enabled: boolean;
}> {
  const client = getAPIClient();
  return client.get(`/tenants/${tenantId}/config/azure-from-env`);
}





